import React, { useCallback, useContext, useEffect, useState } from 'react'
import { useNavigate } from "react-router-dom";
import { Alert, Box, Container, Grid, Stack, Typography, useTheme } from "@mui/material";
import Steps from "widgets/Steps/Steps";
import { ReactComponent as Logo } from 'assets/images/rein_logo.svg'
import { useFormBackMutationQuery } from 'entities/ApplicationFormBack/hooks/queries';
import { FormDataContext } from 'app/providers/formValues/lib/FormDataContext';
import { usePolicyErrorQuery, usePolicyMutationQuery } from 'entities/Policy/hooks/queries';
import { StyledButton } from "shared/ui/Button/styles";
import {
	useCustomerDataUpdateMutationQuery,
	useCustomerJourneyMutationQuery,
	useJourneyQuery,
	useJourneyResultFormQuery
} from 'entities/ApplicationForm/hooks/queries';
import { useApplicationProcessQuery, useProcessMutationQuery } from 'entities/ApplicationProcess/hooks/queries';
import { policyPayload } from 'widgets/CheckoutForm/helper';
import { ApplicationFormContext } from 'app/providers/applicationForm/lib/ApplicationFormContext';
import SummaryInfo from 'widgets/SummaryInfo';
import { PolicyResponse } from 'shared/api/assurant/policy/types';
import { useResolutionQuery } from 'entities/Resolution/hooks/queries';
import HTMLModal from 'shared/ui/HTMLModal/HTMLModal';
import { DocumentType } from 'shared/config/enums/documents';
import { CustomerFormattedType, JourneyResult } from 'shared/config/types/customerResponse';
import { ErrorDetails } from 'entities/Policy/hooks/types';
import LogoHeader from '../../features/LogoHeader/ui/LogoHeader';
import Payment from 'widgets/CheckoutForm/components/Payment';
import { FormProvider, useForm } from 'react-hook-form';

interface PaymentValues {
	paymentType: "cc" | "bank",
	payment: {
		cardName: string,
		cardNumber: string,
		cardDate: string,
		cardCode: string,
		bankAccountNumber: string,
		bankAccountNumConfirm: string,
		bankRoutingNumber: string,
	},
}

const ACCOUNT_ERROR_MESSAGE = "The Credit Card or Checking Account information cannot be authorized at this time. Please either choose a new payment option or enter a different Credit Card or Checking Account by selecting the EDIT INFORMATION button below."
const SummaryPage = () => {
	const theme = useTheme()
	const navigate = useNavigate();

	const { paymentData, query, formData } = useContext(FormDataContext);
	const { applicationAddress } = useContext(ApplicationFormContext);
	const { plan } = formData;
	const methods = useForm<PaymentValues>({
		defaultValues: {
			paymentType: paymentData?.paymentType || 'cc',
			payment: {
				cardName: paymentData?.payment.cardName || '',
				cardCode: paymentData?.payment.cardCode || '',
				cardDate: paymentData?.payment.cardDate || '',
				cardNumber: paymentData?.payment.cardNumber || '',
				bankAccountNumber: paymentData?.payment.bankAccountNumber || '',
				bankAccountNumConfirm: paymentData?.payment.bankAccountNumConfirm || '',
				bankRoutingNumber: paymentData?.payment.bankRoutingNumber || '',
			}
		}
	})
	const { getValues } = methods;
	const [disclosuresBannerOpen, setDisclosuresBannerOpen] = useState<DocumentType | null>(null);
	const { isSuccess } = useFormBackMutationQuery();
	const { mutate: fetchForm } = useCustomerJourneyMutationQuery();
	const { mutate: getApplicationProcess, isSuccess: processSuccess } = useProcessMutationQuery();
	const { mutate: makePayment, isSuccess: paymentSuccess, isPending, data } = usePolicyMutationQuery();
	const errorDetails: { errorDetails: ErrorDetails } = usePolicyErrorQuery()
	const { mutateAsync: updateData, isPending: isPendingCustomerUpdate } = useCustomerDataUpdateMutationQuery();
	const resolutionQuery = useResolutionQuery();
	const applicationProcessQuery = useApplicationProcessQuery();
	const customerData = useJourneyQuery();
	const journeyResultData = useJourneyResultFormQuery();
	const applicationFormId = localStorage.getItem("applicationFormId");
	const moduleId = localStorage.getItem("moduleId");
	const [accountErrorPayment, setAccountErrorPayment] = useState(false);
	const [editPayment, setEditPayment] = useState(false);
	useEffect(() => {
		if (!paymentData) {
			navigate(-1);
		}
	}, [paymentData])
	const handleClose = () => {
		setDisclosuresBannerOpen(null)
	}
	useEffect(() => {
		if (isSuccess && applicationFormId) {
			getApplicationProcess(applicationFormId);
		}
	}, [isSuccess]);

	useEffect(() => {
		if (processSuccess && applicationFormId) {
			const executionModuleId = applicationProcessQuery?.executingModuleId
			if (
				applicationProcessQuery?.applicationProcessStatus === "WaitingForUserInput"
			) {
				if (executionModuleId !== moduleId) {
					// eslint-disable-next-line @typescript-eslint/no-unused-expressions
					executionModuleId !== undefined && localStorage.setItem("moduleId", executionModuleId);
					fetchForm(applicationFormId);
					navigate(-2);
				} else {
					setTimeout(() => {
						getApplicationProcess(applicationFormId);
					}, 400);
				}
			} else {
				setTimeout(() => {
					getApplicationProcess(applicationFormId);
				}, 400);
			}
		}
	}, [processSuccess]);

	const goBack = () => {
		stepBack()
	}
	const stepBack = () => {
		navigate(-1);
	}
	const onSubmit = () => {
		if (!query) return;
		if (!paymentData) return;
		setAccountErrorPayment(false);
		setEditPayment(false);
		let paymentInfo = paymentData
		if (editPayment) {
			const values = getValues();
			paymentInfo = {
				...paymentData,
				paymentType: values.paymentType,
				payment: values.payment
			}
		}
		const uwAnswers = resolutionQuery?.pmsValues[0].value;
		const policyData = policyPayload(query, paymentInfo!, plan, applicationAddress, uwAnswers)
		makePayment(policyData);
	}

	const updateDataAsync = useCallback(async (data: PolicyResponse, customerData: CustomerFormattedType, journeyResult?: JourneyResult) => {
		const queryNew = {
			...journeyResult,
			policy_result: data,
		}
		try {
			await updateData({
				customerJourneyId: customerData.customerJourneyId,
				stageName: "PolicyPurchase",
				stageId: "",
				model: JSON.stringify(queryNew),
			})
			navigate('/confirmation');
		} catch (e) {
			console.log('e: ', e);
		}
	}, [navigate, updateData]);
	useEffect(() => {
		if (paymentSuccess && data && data.data.transactionDetails.responseMessage) {
			if (data.data.transactionDetails.responseMessage.toLowerCase().includes('duplicate')) {
				window.location.href = 'https://renterscoverage.rein.ai/';
				return
			}
			if (data.data.transactionDetails.responseMessage === ACCOUNT_ERROR_MESSAGE) {
				setAccountErrorPayment(true)
				return
			}
			navigate('/error');
			return
		}
		if (paymentSuccess && data && customerData) {
			void updateDataAsync(data.data, customerData, journeyResultData).then(r => r)
		}
	}, [paymentSuccess])

	const onEdit = () => {
		setEditPayment(true)
	}
	return (
		<Box>
			<Grid
				container
				spacing={0}
				sx={{ minHeight: '100vh' }}
			>
				<Grid size={12}>
					<Box sx={{ textAlign: 'center', position: 'relative' }} mt={5} mb={5}>
						<Typography
							variant="body1"
							color={"primary.light"}
							fontSize={14}
							sx={{ mr: 1, cursor: 'pointer', position: 'absolute' }}
							onClick={stepBack}
						>
							Back
						</Typography>
						<LogoHeader/>
					</Box>
					<Steps/>
					<Container maxWidth={"lg"} sx={{ mt: 5 }}>
						<Box display="flex" alignItems="center" mb={2} justifyContent="center">
							<Typography variant={"h3"} align={"center"} fontSize={42} sx={{ mb: 0 }}>One Last
								Review</Typography>
						</Box>
						<SummaryInfo goBack={goBack} stepBack={stepBack}/>
						{errorDetails && <Stack sx={{ width: '100%' }} spacing={2} mb={2} mt={2}>
							{errorDetails?.errorDetails.error?.errors.map((item) => (
								<Typography variant="subtitle2" fontWeight={400} color={"error.main"}>
									<Alert severity="error">{item.description}</Alert>
								</Typography>
							))}
						</Stack>}
						{accountErrorPayment && (
							<>
								<Box
									border="1px solid"
									borderColor="error.main"
									sx={{
										p: 3,
										mt: 4,
										mb: 2,
										backgroundColor: "background.default",
										borderRadius: 1
									}}
								>
									<Alert
										severity="error"
										sx={{ mb: 3 }}
									>
										<Typography variant="body1">
											{ACCOUNT_ERROR_MESSAGE}
										</Typography>
									</Alert>

									<Box display="flex" justifyContent="center">
										<Box sx={{ width: { xs: '100%', sm: '400px' } }}>
											<StyledButton onClick={onEdit}>
												EDIT INFORMATION
											</StyledButton>
										</Box>
									</Box>
									{editPayment && (
										<Grid container justifyContent="center">
											<Grid size={8} display="flex" alignItems="center" justifyContent="center">
												<FormProvider {...methods}>
													<Payment/>
												</FormProvider>
											</Grid>
										</Grid>
									)}
								</Box>
							</>
						)}
						<Grid container mt={4}>
							<Grid size={12} justifyContent="center" alignItems="center" display="flex">
								<StyledButton
									sx={{ maxWidth: '350px' }}
									loading={isPending || isPendingCustomerUpdate}
									variant="outlined"
									onClick={onSubmit}
								>
									PURCHASE NOW
								</StyledButton>
							</Grid>
						</Grid>

						<Box sx={{ mt: 5, mb: 5 }}>
							<Typography variant="body1" sx={{
								fontSize: '14px',
								lineHeight: '20px',
								'& a': {
									color: '#2780EC',
									cursor: 'pointer',
									textDecoration: 'none'
								}
							}}>
								By clicking "Purchase Now", I acknowledge that I have read and understand all applicable
								<a onClick={() => setDisclosuresBannerOpen("underwriters")}> underwriting</a>,
								<a onClick={() => setDisclosuresBannerOpen("legal")}> state disclosures</a>,
								<a onClick={() => setDisclosuresBannerOpen("fraud")}> fraud notices </a> &
								<a onClick={() => setDisclosuresBannerOpen("cancelationnotice")}> notice
									of
									cancellation</a>,
								and <a onClick={() => setDisclosuresBannerOpen("payauthorization")}>recurring payment
								authorization</a>.
							</Typography>
						</Box>
						{theme.palette.assetUrl &&
							<Box textAlign="center" mb={2}><Logo width={171} height={40}/>
								<Typography variant="body1" fontSize="10px" lineHeight="12px">Renters Insurance powered
									by
									Assurant®</Typography></Box>
						}
					</Container>
				</Grid>
			</Grid>
			{disclosuresBannerOpen && <HTMLModal
				open={!!disclosuresBannerOpen}
				handleClose={handleClose}
				type={disclosuresBannerOpen}
			/>}
		</Box>
	)
}

export default SummaryPage
